import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  fetchWithAuth,
  fetchWithAuthUpload,
} from "../../../Context/UserContext";
import PageLayout from "../../../components/layout/PageLayout";
import { LoadingOverlay } from "../../../components/ui/loading-overlay";
import API_URL from "../../../config";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";

const PostulacionCompleta = () => {
  const { tenderId, applicationId } = useParams();
  const [postulacion, setPostulacion] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const closeModal = () => setModalIsOpen(false);

  useEffect(() => {
    if (tenderId && applicationId) {
      fetchPostulacion(tenderId, applicationId);
    }
  }, [tenderId, applicationId]);

  const fetchPostulacion = async (tenderId, applicationId) => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/licit/tender/${tenderId}/postulacion/${applicationId}`
      );
      if (response.ok) {
        const data = await response.json();

        // Procesar respuestas de preguntas para incluir archivos adjuntos
        let respuestasProcesadas = {
          ...data.postulacion.respuestas_preguntas_licitador,
        };

        // Si la respuesta es "", asignamos el archivo correspondiente
        Object.entries(data.postulacion.archivos_respuestas || {}).forEach(
          ([idPregunta, archivo]) => {
            if (respuestasProcesadas[idPregunta] === "") {
              respuestasProcesadas[idPregunta] = {
                file_name: archivo.file_name,
                file_path: archivo.file_path,
                id_file: archivo.id_file,
              };
            }
          }
        );

        // Actualizar el estado con los datos procesados
        setPostulacion({
          ...data.postulacion,
          respuestas_preguntas_licitador: respuestasProcesadas,
        });
      } else {
        console.error("Error al obtener la postulación");
      }
    } catch (error) {
      console.error("Error al obtener la postulación:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadFile = async (fileId) => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuthUpload(
        `${API_URL}/upload/download-file/${fileId}`,
        { method: "GET" }
      );

      if (!response.ok) {
        throw new Error("Error al obtener el archivo");
      }

      const data = await response.json();
      window.open(data.file_url, "_blank");
    } catch (error) {
      console.error("Error al descargar archivo:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageLayout>
      {!postulacion ? (
        <LoadingOverlay />
      ) : (
        <div className="mx-auto max-w-3xl space-y-4 px-4 py-8 sm:px-6">
          <h3 className="text-xl font-semibold text-foreground">Información de la Postulación</h3>

          {/* Información del usuario postulante */}
          <Card>
            <CardContent className="space-y-2 pt-6">
              <h4 className="font-semibold text-foreground">Datos del Postulante</h4>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Número de postulación:</strong>{" "}
                {postulacion.id_application}
              </p>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Nombre:</strong>{" "}
                {postulacion.user_postulante ? (
                  <Link
                    to={`/profile/${postulacion.user_postulante.id_user}/${postulacion.user_postulante.nombre}-${postulacion.user_postulante.apellido}`}
                    className="text-primary hover:underline"
                  >
                    {`${postulacion.user_postulante.nombre} ${postulacion.user_postulante.apellido}`}
                  </Link>
                ) : (
                  "Usuario no disponible"
                )}
              </p>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Correo:</strong> {postulacion.user_postulante.correo}
              </p>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Teléfono:</strong> +{postulacion.user_postulante.codigo_pais}{" "}
                {postulacion.user_postulante.celular}
              </p>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Compañía:</strong>{" "}
                {postulacion.company_postulante ? (
                  <Link
                    to={`/company-profile/${postulacion.company_postulante.id_company}/${postulacion.company_postulante.nombre_empresa}`}
                    className="text-primary hover:underline"
                  >
                    {postulacion.company_postulante.nombre_empresa}
                  </Link>
                ) : (
                  "---"
                )}
              </p>
            </CardContent>
          </Card>

          {/* Oferta */}
          <Card>
            <CardContent className="space-y-2 pt-6">
              <h4 className="font-semibold text-foreground">Oferta</h4>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Detalles:</strong> {postulacion.detalles_oferta}
              </p>
            </CardContent>
          </Card>

          {/* Documentos Adjuntos */}
          {postulacion.archivos_adjuntos && postulacion.archivos_adjuntos.length > 0 && (
            <Card>
              <CardContent className="space-y-2 pt-6">
                <h4 className="font-semibold text-foreground">Documentos Adjuntos</h4>
                <ul className="space-y-1">
                  {postulacion.archivos_adjuntos.map((archivo, index) => (
                    <li key={index}>
                      <button
                        onClick={() => downloadFile(archivo.id_file)}
                        title={archivo.file_name}
                        aria-label={`Descargar ${archivo.file_name}`}
                        className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                      >
                        <FileText className="h-4 w-4" />
                        {archivo.file_name.length > 30
                          ? `${archivo.file_name.slice(0, 27)}...`
                          : archivo.file_name}
                      </button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Respuestas a Preguntas */}
          {Object.entries(postulacion.respuestas_preguntas_licitador || {}).length > 0 && (
            <Card>
              <CardContent className="space-y-3 pt-6">
                <h4 className="font-semibold text-foreground">Respuestas a Preguntas</h4>
                <ul className="space-y-3">
                  {Object.entries(postulacion.respuestas_preguntas_licitador).map(
                    ([pregunta, respuesta], index) => (
                      <li key={index} className="text-sm">
                        <p className="font-medium text-foreground">
                          {index + 1}. {pregunta}
                        </p>
                        <p className="mt-0.5 text-muted-foreground">
                          {typeof respuesta === "object" && respuesta?.file_name ? (
                            <button
                              onClick={() => downloadFile(respuesta.id_file)}
                              title={respuesta.file_name}
                              className="flex items-center gap-1.5 text-primary hover:underline"
                            >
                              <FileText className="h-4 w-4" />
                              {respuesta.file_name.length > 20
                                ? `${respuesta.file_name.slice(0, 17)}...`
                                : respuesta.file_name}
                            </button>
                          ) : (
                            respuesta || "No respondida"
                          )}
                        </p>
                      </li>
                    )
                  )}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Estado y Fecha */}
          <Card>
            <CardContent className="space-y-2 pt-6">
              <h4 className="font-semibold text-foreground">Información Adicional</h4>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Estado:</strong> {postulacion.estado}
              </p>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Fecha de Creación:</strong>{" "}
                {new Date(postulacion.created_at).toLocaleString()}
              </p>
            </CardContent>
          </Card>

          <Button
            variant="outline"
            onClick={() => (window.location.href = `/ver-postulaciones/${postulacion.id_tender}`)}
          >
            Regresar
          </Button>
        </div>
      )}

      <Dialog open={modalIsOpen} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent>
          {!postulacion ? (
            <LoadingOverlay />
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Información de Contacto</DialogTitle>
              </DialogHeader>
              <div className="space-y-1 text-sm text-muted-foreground">
                <p>
                  <strong className="text-foreground">Nombre:</strong>{" "}
                  {postulacion.user_postulante
                    ? `${postulacion.user_postulante.nombre} ${postulacion.user_postulante.apellido}`
                    : "Información no disponible"}
                </p>
                <p>
                  <strong className="text-foreground">Compañía:</strong>{" "}
                  {postulacion.company_postulante ? postulacion.company_postulante.nombre_empresa : "---"}
                </p>
                <p>
                  <strong className="text-foreground">Correo:</strong>{" "}
                  {postulacion.user_postulante ? postulacion.user_postulante.correo : "Información no disponible"}
                </p>
                <p>
                  <strong className="text-foreground">Teléfono:</strong>{" "}
                  {postulacion.user_postulante
                    ? `+${postulacion.user_postulante.codigo_pais} ${postulacion.user_postulante.celular}`
                    : "Información no disponible"}
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeModal}>
                  Cerrar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default PostulacionCompleta;
